import React, {useMemo, useState} from "react";
import {Accordion, Badge, Card} from "react-bootstrap";
import {IMatchModel} from "../../types/pinecone";
import {useConfigState} from "../../hooks";

const EXCLUDED_METADATA_KEYS = ["ArtNo", "DocType", "DocName", "DocNo", "FileName"];

interface IMetadataFilterCardProps {
    pineconeMatches: IMatchModel[];
    setMetadata: (metadata: Record<string, { $in: string[] }>) => void;
}

const MetadataFilterCard: React.FC<IMetadataFilterCardProps> = ({pineconeMatches, setMetadata}) => {
    const {metadataSelector} = useConfigState();
    const [selectedMetadata, setSelectedMetadata] = useState<Record<string, string[]>>({});

    const allMetadata = useMemo(() => pineconeMatches.map((item) => item.metadata), [pineconeMatches]);

    const metadataKeys = useMemo(
        () => Array.from(new Set(allMetadata.flatMap((metadata) => Object.keys(metadata)))),
        [allMetadata]
    );

    const filteredMetadataKeys = useMemo(
        () => metadataKeys.filter((key) => !EXCLUDED_METADATA_KEYS.includes(key)),
        [metadataKeys]
    );

    const uniqueMetadataValues = useMemo(() => {
        const metadataValues: Record<string, string[]> = {};
        filteredMetadataKeys.forEach((key) => {
            metadataValues[key] = Array.from(
                new Set(pineconeMatches.flatMap((item) => item.metadata[key as keyof typeof item.metadata] || []))
            );
        });
        return metadataValues;
    }, [filteredMetadataKeys, pineconeMatches]);

    const handleBadgeClick = (key: string, value: string) => {
        setSelectedMetadata((prevSelected) => {
            const updatedSelection = {
                ...prevSelected,
                [key]: prevSelected[key]?.includes(value)
                    ? prevSelected[key].filter((val) => val !== value)
                    : [...(prevSelected[key] || []), value],
            };

            const filterObject = Object.entries(updatedSelection).reduce<Record<string, { $in: string[] }>>(
                (acc, [metadataKey, selectedValues]) => {
                    if (selectedValues.length > 0) {
                        acc[metadataKey] = {$in: selectedValues};
                    }
                    return acc;
                },
                {}
            );

            setMetadata(filterObject);
            return updatedSelection;
        });
    };

    return metadataSelector && (
        <Accordion defaultActiveKey="0">
            <Accordion.Item eventKey="0">
                <Accordion.Header>Metadata Selection</Accordion.Header>
                <Accordion.Body>
                    {filteredMetadataKeys.map((key) => (
                        <Card className="position-relative mb-2" key={key}>
                            <Card.Body>
                                <div className="d-flex flex-row gap-1 align-items-baseline">
                                    <div>{key}:</div>
                                    <div>
                                        {uniqueMetadataValues[key].map((value) => {
                                            const isSelected = selectedMetadata[key]?.includes(value);
                                            return (
                                                <Badge
                                                    key={value}
                                                    pill
                                                    bg={isSelected ? "primary" : "secondary"}
                                                    className="me-1 badge-clickable"
                                                    onClick={() => handleBadgeClick(key, value)}
                                                    style={{cursor: "pointer"}}
                                                >
                                                    {value}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

export default MetadataFilterCard;
